import { displayText } from "../../locales/display";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Input from "@ui/input/Input";
import { Page } from "@ui/page/Page";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { Table } from "@ui/table/Table";
import { Typo } from "@ui/text/Typo";
import Panel from "@ui/panel/Panel";
import { GD } from "../../GD";
import { notify } from "../../tools/notify";
import { S_USER_EDITOR_POPUP } from "../popup/UserEditor";
import { S_INVITE_USER_POPUP } from "../popup/InviteUserPopup";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { checkFeature, TAvailableFeatures, TFeaturedComponent } from "src/Featuresmap";
import { forwardRef, MutableRefObject, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { S_API_USER_POPUP } from "../popup/ApiUserPopup";
import { IClient } from "src/services/ClientService";
import Signal from "badmfck-signal";
import { ChevronDown, ChevronUp, Filter as FilterIcon, KeyRound, MailPlus, ShieldCheck, UserCog, UsersRound } from "lucide-react";
import { S_CONFIRM } from "../popup/general/ConfirmDialog";
import { CountryFlag } from "../conponents/icons/CountryFlag";
import { hasAnyRole } from "../../permissions";
import { createMembershipUpdater, membershipLabel, membershipStatus } from "./userProjectMembership";

const TAB_MANAGEMENT = 0;
const TAB_ALL_USERS = 1;
const TAB_INVITES = 2;
const TAB_API_USERS = 3;

type TUserFilterOption = {
	name: string;
	value: string;
};

type TClientFilterOption = Partial<IClient> & {
	id: number;
	name: string;
	isAll?: boolean;
};

type TAllUser = IUser & {
	client?: unknown;
	clients?: unknown;
	projects?: unknown;
	[key: string]: unknown;
};

type TUserInfoPopupProps = {
	user: TAllUser;
	projectId: number;
	onRolesUpdated: () => void | Promise<void>;
};

type TUserClientLinkPopupProps = {
	user: TAllUser;
	linkedClients: TClientFilterOption[];
	onChanged: (linkedClients: TClientFilterOption[]) => void | Promise<void>;
};

type TUserInfoRole = IUserRole & { name?: string; description?: string };

const allClientOption: TClientFilterOption = { id: 0, get name() { return msg("allClients"); }, isAll: true };
const allRoleOption: TUserFilterOption = { get name() { return msg("allRoles"); }, value: "" };
const S_USER_INFO_POPUP = new Signal<TUserInfoPopupProps | null>("S_USER_INFO_POPUP");
const S_USER_CLIENT_LINK_POPUP = new Signal<TUserClientLinkPopupProps | null>("S_USER_CLIENT_LINK_POPUP");

type TInvitesHandle = {
	update: () => void;
};

export const UsersPage = () => {
	useLocale();
	const [activeTab, setActiveTab] = useState(0);

	const invites = useRef<TInvitesHandle | null>(null);
	const apiUsersReload = useRef<() => void>(() => {});

	const onInvite = () => {
		S_INVITE_USER_POPUP.invoke({
			onSuccess: () => {
				(invites.current as any)?.update();
			},
		});
	};

	const onCreateApiUser = () => {
		S_API_USER_POPUP.invoke({ onSuccess: () => apiUsersReload.current() });
	};

	const sectionRightContent =
		activeTab === TAB_API_USERS ? (
			<Button onClick={onCreateApiUser} title={msg("createApiUser")} variant="primarySmall" />
		) : (
			<Button onClick={onInvite} title={msg("inviteUser")} variant="primarySmall" />
		);

	return (
		<Page
			label={msg("users")}
			description={msg("manageTeamAccessInvitationsRolesAndApiUsers")}
			icon={<UserCog size={20} />}
			rightSection={<RightSectionUsers />}
			sectionRightContent={sectionRightContent}
			sx={{ backgroundColor: "transparent", boxShadow: "none", padding: "0px" }}>
			<VBox sx={{ height: "100%" }}>
				<Tabs variant="modern" checkFeature={feature => checkFeature(feature as TAvailableFeatures)} onSectionSelect={setActiveTab}>
					<TabItem title={msg("management")} section={TAB_MANAGEMENT} icon={<ShieldCheck size={16} />}>
						<Spacer variant="small" />
						<ManagementTab />
					</TabItem>
					<TabItem title={msg("allUsers")} section={TAB_ALL_USERS} icon={<UsersRound size={16} />}>
						<Spacer variant="small" />
						<AllUsersTab />
					</TabItem>
					<TabItem title={msg("invites")} section={TAB_INVITES} icon={<MailPlus size={16} />}>
						<Spacer variant="small" />
						<InvitesTab invitesRef={invites} />
					</TabItem>
					<TabItem title={msg("apiUsers")} section={TAB_API_USERS} icon={<KeyRound size={16} />}>
						<Spacer variant="small" />
						<ApiUsersTab reloadRef={apiUsersReload} />
					</TabItem>
				</Tabs>
			</VBox>
			<UserInfoPopup />
			<UserClientLinkPopup />
		</Page>
	);
};

const getUserListRequest = (search: string, clientFilter: TClientFilterOption, roleFilter: TUserFilterOption, baseRequest: Record<string, unknown> = {}) => {
	return {
		...baseRequest,
		search: search.trim() || undefined,
		clientID: clientFilter.isAll ? undefined : clientFilter.id,
		roles: roleFilter.value ? [Number(roleFilter.value)] : undefined,
	};
};

const getClientId = (value: unknown) => {
	const id = (value as { id?: unknown })?.id;
	if (typeof id === "number" && Number.isFinite(id)) return id;
	if (typeof id === "string" && /^\d+$/.test(id)) return Number(id);
	return undefined;
};

const isDeletedClient = (value: unknown) => {
	const status = (value as { status?: unknown })?.status;
	return typeof status === "string" && status.toLowerCase() === "deleted";
};

const normalizeClientFilterOptions = (value: unknown): TClientFilterOption[] => {
	const items = Array.isArray(value) ? value : value && typeof value === "object" ? Object.values(value) : [];

	return items.flatMap(item => {
		if (typeof item !== "object" || item === null) return [];
		if (isDeletedClient(item)) return [];

		const id = getClientId(item);
		if (id === undefined) return [];

		const client = item as Partial<IClient> & { name?: unknown };
		const name = typeof client.name === "string" && client.name.trim() ? client.name : msg("templateClient_2fb386d5", { v0: id });

		return [{ ...client, id, name }];
	});
};

const getClientLabel = (client?: TClientFilterOption) => {
	if (!client) return msg("client");
	if (client.isAll) return msg("allClients");
	return client.name || msg("templateClient_2fb386d5", { v0: client.id });
};

const ClientCountryFlag = ({ client }: { client: TClientFilterOption }) => {
	useLocale();
	if (!client.country) return null;
	return <CountryFlag code={client.country} variant="rectangle" />;
};

const getFilterOptionByValue = (options: TUserFilterOption[], value: string): TUserFilterOption | undefined => {
	return options.find(option => option.value === value);
};

const getStatusColor = (status?: string) => {
	if (status === "active" || status === "approved") return { bg: "#E8F7EF", color: "#147A45" };
	if (status === "blocked" || status === "suspended") return { bg: "#FCECEC", color: "#B42318" };
	if (status === "deleted" || status === "disabled") return { bg: "#F1F5F9", color: "#64748B" };
	return { bg: "#FFF7E8", color: "#A15C07" };
};

const getUserClients = (user: TAllUser) => {
	const values: unknown[] = [];

	if (user.client) values.push(user.client);
	if (user.clients) values.push(user.clients);
	if (user.projects) values.push(user.projects);

	Object.keys(user).forEach(key => {
		if (/^\d+$/.test(key)) values.push(user[key]);
	});

	const clients = values.flatMap(normalizeClientFilterOptions);
	const uniqueClients = new Map<number, TClientFilterOption>();
	clients.forEach(client => uniqueClients.set(client.id, client));

	return Array.from(uniqueClients.values());
};

const formatUserDate = (value?: string) => {
	if (!value) return "-";
	return formatDisplayDate(value, { locale: "en-GB", fallback: value });
};

const getUserDisplayName = (user: TAllUser) => {
	if (user.name) return user.name;
	if (user.email) return user.email.split("@")[0];
	return msg("templateUser_039247f9", { v0: user.id });
};

const getFieldLabel = (value?: string | null) => {
	if (!value) return "-";
	return value.replace(/_/g, " ");
};

const AllUsersStatusBadge = ({ status, prefix }: { status?: string; prefix?: string }) => {
	useLocale();
	const statusColors = getStatusColor(status);

	return (
		<Typo
			sx={{
				display: "inline-flex",
				alignItems: "center",
				backgroundColor: statusColors.bg,
				color: statusColors.color,
				borderRadius: "4px",
				padding: "3px 8px",
				fontSize: "11px",
				fontWeight: 600,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				whiteSpace: "nowrap",
			}}>
			{prefix ? `${prefix}: ${getFieldLabel(status)}` : getFieldLabel(status)}
		</Typo>
	);
};

const AllUsersAction = ({ onClick }: { onClick: () => void }) => {
	useLocale();
	return (
		<Box
			onClick={event => {
				event?.stopPropagation();
				onClick();
			}}
			sx={{
				width: "24px",
				height: "26px",
				borderRadius: "6px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: "#5F5D59",
				cursor: "pointer",
				"&:hover": { backgroundColor: "#F0ECE8", color: "#1A1917" },
			}}>
			<HBox gap="2px" sx={{ alignItems: "center", justifyContent: "center" }}>
				<Box sx={{ width: "3px", height: "3px", borderRadius: "50%", backgroundColor: "currentColor" }} />
				<Box sx={{ width: "3px", height: "3px", borderRadius: "50%", backgroundColor: "currentColor" }} />
				<Box sx={{ width: "3px", height: "3px", borderRadius: "50%", backgroundColor: "currentColor" }} />
			</HBox>
		</Box>
	);
};

const UserActionMenu = ({ onView, onRemove, removed }: { onView: () => void; onRemove: () => void; removed: boolean }) => {
	useLocale();
	const [open, setOpen] = useState(false);
	const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
	const ref = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		const handler = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node) && menuRef.current && !menuRef.current.contains(event.target as Node))
				setOpen(false);
		};
		const scrollHandler = () => setOpen(false);
		document.addEventListener("mousedown", handler);
		document.addEventListener("scroll", scrollHandler, true);
		return () => {
			document.removeEventListener("mousedown", handler);
			document.removeEventListener("scroll", scrollHandler, true);
		};
	}, [open]);

	const itemSx = {
		padding: "8px 16px",
		cursor: "pointer",
		whiteSpace: "nowrap" as const,
		transition: "background-color 0.15s ease",
		"&:hover": { backgroundColor: "#f0f5ff" },
	};

	return (
		<Box ref={ref} sx={{ position: "relative" }}>
			<AllUsersAction
				onClick={() => {
					if (ref.current) {
						const rect = ref.current.getBoundingClientRect();
						const menuHeight = 82;
						const openUp = rect.bottom + menuHeight > window.innerHeight;
						setMenuPos({
							top: openUp ? rect.top - menuHeight - 4 : rect.bottom + 4,
							left: rect.right,
						});
					}
					setOpen(value => !value);
				}}
			/>
			{open &&
				createPortal(
					<VBox
						ref={menuRef}
						sx={{
							position: "fixed",
							top: menuPos.top + "px",
							left: menuPos.left + "px",
							transform: "translateX(-100%)",
							zIndex: 10000,
							backgroundColor: "#fff",
							borderRadius: "10px",
							boxShadow: "0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
							minWidth: "150px",
							overflow: "hidden",
							border: "1px solid #e5e7eb",
						}}>
						<Box
							sx={itemSx}
							onClick={event => {
								event?.stopPropagation();
								setOpen(false);
								onView();
							}}>
							<Typo variant="medium" sx={{ fontWeight: 500 }}>{msg("view")}</Typo>
						</Box>
						<Box sx={{ height: "1px", backgroundColor: "#f0f0f0", margin: "0 12px" }} />
						<Box
							sx={{ ...itemSx, "&:hover": { backgroundColor: "#fef2f2" } }}
							onClick={event => {
								event?.stopPropagation();
								setOpen(false);
								onRemove();
							}}>
							<Typo variant="medium" sx={{ fontWeight: 500, color: removed ? "#147A45" : "#DC2626" }}>
								{removed ? msg("restoreToProject") : msg("removeFromProject")}
							</Typo>
						</Box>
					</VBox>,
					document.body,
				)}
		</Box>
	);
};

const TableActionButton = ({ title, variant, onClick }: { title: string; variant?: "danger"; onClick: () => void }) => {
	useLocale();
	return (
		<Box
			onClick={event => {
				event?.stopPropagation();
				onClick();
			}}
			sx={{
				height: "26px",
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "0 10px",
				borderRadius: "6px",
				border: variant === "danger" ? "1px solid #f0c8c0" : "1px solid #dccfc7",
				backgroundColor: "#fff",
				color: variant === "danger" ? "#b42318" : "#5f5d59",
				fontSize: "11px",
				fontWeight: 600,
				cursor: "pointer",
				whiteSpace: "nowrap",
				"&:hover": { backgroundColor: variant === "danger" ? "#fff1ee" : "#f8f0ed" },
			}}>
			{title}
		</Box>
	);
};

const ExpandPill = ({ label, icon, onClick }: { label: string; icon?: JSX.Element; onClick: () => void }) => { useLocale(); return ((
	<Box
		onClick={onClick}
		title={msg("templateExpand_614e2df2", { v0: label.toLowerCase() })}
		sx={{
			display: "inline-flex",
			alignItems: "center",
			gap: "6px",
			padding: "0 12px",
			height: "36px",
			borderRadius: "6px",
			border: "1px solid #cbd5e1",
			backgroundColor: "#fff",
			cursor: "pointer",
			color: "#475569",
			fontSize: "13px",
			fontWeight: 500,
			whiteSpace: "nowrap",
			flexShrink: 0,
			transition: "background-color 0.15s, color 0.15s, border-color 0.15s",
			"&:hover": { backgroundColor: "#f8fafc", color: "#1e293b", borderColor: "#94a3b8" },
		}}>
		{icon}
		{label}
		<ChevronDown size={14} />
	</Box>
)); };

const usersTableSx = { backgroundColor: "#f8fafc", width: "100%" };
const usersTableHeadSx = { backgroundColor: "#e3dfd6", borderBottom: "none" };
const usersTableHeaderLabels: Record<string, string> = {
	__actions: "",
	email_status: "email",
	two_fa: "2fa",
	created_at: "created",
	updated_at: "updated",
	user_type: "type",
	get key_id() { return msg("keyId"); },
	merchants_cnt: "merchants",
	groups_cnt: "groups",
};
const usersTableHeaderFactory = (val: string | JSX.Element | null) => {
	if (!val) return { content: <div style={{ width: "32px" }} /> };
	if (typeof val === "string") {
		const label = usersTableHeaderLabels[val] ?? val;
		return {
			content: (
				<Typo
					variant="medium"
					sx={{
						fontWeight: 400,
						fontSize: "11px",
						fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
						textTransform: "uppercase",
						letterSpacing: "0.06em",
						whiteSpace: "nowrap",
						color: "#000",
					}}>
					{label.toUpperCase()}
				</Typo>
			),
		};
	}
	return { content: null };
};

const AllUsersClientsCell = ({ clients }: { clients: TClientFilterOption[] }) => {
	useLocale();
	if (clients.length === 0) return <Typo sx={{ color: "#8b8580", fontSize: "12px" }}>{msg("noClient")}</Typo>;

	const primaryClient = clients[0];
	const hiddenClientsCount = clients.length - 1;
	const secondaryClientNames = clients
		.slice(1, 3)
		.map(client => getClientLabel(client))
		.join(", ");
	const meta =
		clients.length === 1
			? primaryClient.status && primaryClient.status.toLowerCase() !== "active"
				? msg("templateClient_482c6cf8", { v0: getFieldLabel(primaryClient.status) })
				: null
			: secondaryClientNames
				? msg("templateClients_774ca370", { v0: clients.length, v1: secondaryClientNames, v2: clients.length > 3 ? ", ..." : "" })
				: msg("templateClients_318935b4", { v0: clients.length });
	return (
		<Box title={clients.map(getClientLabel).join("\n")} sx={{ minWidth: 0 }}>
			<HBox gap="6px" sx={{ alignItems: "center", minWidth: 0 }}>
				<ClientCountryFlag client={primaryClient} />
				<Typo
					sx={{
						maxWidth: "150px",
						backgroundColor: "#f8f0ed",
						border: "1px solid #eadcd5",
						borderRadius: "6px",
						padding: "2px 7px",
						color: "#2c2926",
						fontSize: "12px",
						fontWeight: 600,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}>
					{getClientLabel(primaryClient)}
				</Typo>
				{hiddenClientsCount > 0 && (
					<Typo
						sx={{
							border: "1px solid #cbd5e1",
							borderRadius: "6px",
							padding: "2px 7px",
							color: "#475569",
							fontSize: "12px",
							fontWeight: 600,
							backgroundColor: "#fff",
							whiteSpace: "nowrap",
						}}>
						+{hiddenClientsCount}
					</Typo>
				)}
			</HBox>
			{meta && (
				<Typo sx={{ color: "#8b8580", fontSize: "11px", lineHeight: "15px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{meta}
				</Typo>
			)}
		</Box>
	);
};

const AllUsersTable = ({
	users,
	busy,
	error,
	onEdit,
	onRemove,
}: {
	users: TAllUser[];
	busy?: boolean;
	error?: IError;
	onEdit: (user: TAllUser) => void;
	onRemove?: (user: TAllUser) => void;
}) => {
	useLocale();
	type TUserTableRow = TAllUser & { user: string; client: string; email_status: string; two_fa: string; __actions: string };
	const tableData: TUserTableRow[] = users.map(user => ({ ...user, user: "", client: "", email_status: "", two_fa: "", __actions: "" }));

	return (
		<VBox>
			<Error error={error} />
			{busy && tableData.length === 0 ? (
				<Typo sx={{ color: "#6b6762", fontSize: "13px", padding: "30px" }}>{msg("loadingUsers")}</Typo>
			) : (
				<Table<TUserTableRow>
					headPadding="14px 12px"
					cellPadding="8px 12px"
					headSx={usersTableHeadSx}
					sx={usersTableSx}
					scrollable={false}
					headers={[
						{ value: "user", gridColumn: "minmax(190px, 1.1fr)" },
						{ value: "client", gridColumn: "minmax(160px, 1fr)" },
						{ value: "roles", gridColumn: "minmax(170px, 1fr)" },
						{ value: "email_status", gridColumn: "max-content" },
						{ value: "two_fa", gridColumn: "max-content" },
						{ value: "status", gridColumn: "max-content" },
						{ value: "created_at", gridColumn: "max-content" },
						{ value: "__actions", gridColumn: "max-content" },
					]}
					order={["user", "client", "roles", "email_status", "two_fa", "status", "created_at", "__actions"]}
					gridTemplateColumns="minmax(190px, 1.1fr) minmax(160px, 1fr) minmax(170px, 1fr) max-content max-content max-content max-content max-content"
					headerFactory={usersTableHeaderFactory}
					onRowClick={row => onEdit(row)}
					cellFactory={(user, cell, _idx, _key, name) => {
						if (name === "user") {
							return {
								content: (
									<VBox gap="2px">
										<Typo sx={{ color: "#111827", fontSize: "13px", fontWeight: 500, lineHeight: "18px" }}>{getUserDisplayName(user)}</Typo>
										<Typo sx={{ color: "#6b7280", fontSize: "11px", lineHeight: "15px" }}>{user.email}</Typo>
									</VBox>
								),
							};
						}
						if (name === "client") return { content: <AllUsersClientsCell clients={getUserClients(user)} /> };
						if (name === "roles")
							return {
								content:
									user.roles && user.roles.length > 0 ? (
										<Roles roles={user.roles} />
									) : (
										<Typo sx={{ color: "#8b8580", fontSize: "12px" }}>{msg("noRoles")}</Typo>
									),
							};
						if (name === "email_status")
							return {
								content: (
									<Typo sx={{ color: "#6b7280", fontSize: "12px", textTransform: "capitalize" }}>{getFieldLabel(user.status_email)}</Typo>
								),
							};
						if (name === "two_fa")
							return {
								content: <Typo sx={{ color: "#6b7280", fontSize: "12px", textTransform: "capitalize" }}>{getFieldLabel(user.status_2fa)}</Typo>,
							};
						if (name === "status") return { content: <VBox gap="5px">
							<AllUsersStatusBadge status={user.status} prefix="Account" />
							<Typo sx={{ fontSize: "11px", color: membershipStatus(user.project_status) === "deleted" ? "#b42318" : "#6b7280" }}>{membershipLabel(user.project_status)}</Typo>
						</VBox> };
						if (name === "created_at")
							return { content: <Typo sx={{ color: "#6b7280", fontSize: "12px" }}><DateTimeTooltip value={user.created_at}>{formatUserDate(user.created_at)}</DateTimeTooltip></Typo> };
						if (name === "__actions") {
							return {
								content: onRemove && membershipStatus(user.project_status) !== "unknown" ? (
									<UserActionMenu onView={() => onEdit(user)} onRemove={() => onRemove(user)} removed={membershipStatus(user.project_status) === "deleted"} />
								) : (
									<AllUsersAction onClick={() => onEdit(user)} />
								),
							};
						}
						return { content: cell };
					}}
					data={tableData}
				/>
			)}
		</VBox>
	);
};

const getRoleLabel = (role: Partial<IUserRole> & { name?: string }) => displayText(role.role ?? role.name ?? msg("templateRole_5cd184f9", { v0: role.id ?? "-" }));

const getUserInitials = (user: TAllUser) => {
	const source = getUserDisplayName(user) || user.email || "User";
	return (
		source
			.split(" ")
			.filter(Boolean)
			.slice(0, 2)
			.map(part => part[0]?.toUpperCase())
			.join("") || "US"
	);
};

const isRenderableInfoValue = (value: unknown): value is string | number | boolean => {
	return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
};

const formatInfoValue = (value: unknown) => {
	if (value === null || value === undefined || value === "") return "-";
	if (typeof value === "boolean") return value ? "Yes" : "No";
	return String(value);
};

const UserInfoRow = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => {
	useLocale();
	return (
		<HBox sx={{ padding: "9px 0", borderBottom: "1px solid #f0ece8", alignItems: "flex-start", gap: "12px", "&:last-child": { borderBottom: "none" } }}>
			<Typo sx={{ flex: "0 0 140px", color: "#7b746e", fontSize: "12px", fontWeight: 600 }}>{label}</Typo>
			<Typo sx={{ flex: 1, color: "#1f1d1b", fontSize: "13px", lineHeight: "18px", fontFamily: mono ? "monospace" : undefined, minWidth: 0 }}>
				{value}
			</Typo>
		</HBox>
	);
};

const UserInfoSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
	useLocale();
	return (
		<Box
			sx={{
				backgroundColor: "#fff",
				border: "1px solid #e6ddd8",
				borderRadius: "12px",
				padding: "14px 16px",
				boxShadow: "0 6px 18px rgba(59, 43, 32, 0.05)",
			}}>
			<Typo sx={{ color: "#4a4a4a", fontSize: "11px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>{title}</Typo>
			<Spacer size="8px" />
			{children}
		</Box>
	);
};

const UserInfoBadge = ({ label }: { label: string }) => {
	useLocale();
	return (
		<Typo
			sx={{
				backgroundColor: "#f8f0ed",
				border: "1px solid #eadcd5",
				borderRadius: "999px",
				padding: "4px 9px",
				color: "#2c2926",
				fontSize: "12px",
				fontWeight: 600,
			}}>
			{label}
		</Typo>
	);
};

const getDetailedUserPayload = (payload: unknown): Record<string, unknown> => {
	if (!payload || typeof payload !== "object") return {};
	const record = payload as Record<string, unknown>;
	if (record.user && typeof record.user === "object") return record.user as Record<string, unknown>;
	if (record.User && typeof record.User === "object") return record.User as Record<string, unknown>;
	return record;
};

const getDetailedRoles = (payload: unknown, fallbackRoles?: IUserRole[]): TUserInfoRole[] => {
	if (payload && typeof payload === "object") {
		const roles = (payload as Record<string, unknown>).roles;
		if (Array.isArray(roles)) return roles as TUserInfoRole[];
	}
	return fallbackRoles ?? [];
};

const loadUserInfoDetails = async (userID: number, projectId: number) => {
	return Promise.all([
		GD.REQ_API.request({
			method: "users/get",
			data: { id: userID },
			apiVersion: 2,
			expectedProjectId: projectId,
		}),
		GD.REQ_API.request({
			method: "users/roles/get",
			data: { user_id: userID },
			apiVersion: 2,
			expectedProjectId: projectId,
		}),
	]);
};

function UserInfoPopup() {
	useLocale();
	const request = S_USER_INFO_POPUP.use();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const requestRef = useRef(request);
	requestRef.current = request;
	const [membershipBusy, setMembershipBusy] = useState(false);
	const [details, setDetails] = useState<IAPIResponse | null>(null);
	const [userDetails, setUserDetails] = useState<IAPIResponse | null>(null);
	const [clientOverride, setClientOverride] = useState<TClientFilterOption[] | null>(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!request) return;
		if (project?.id !== request.projectId) { S_USER_INFO_POPUP.invoke(null); return; }

		let cancelled = false;
		setBusy(true);
		setDetails(null);
		setUserDetails(null);
		setClientOverride(null);

		loadUserInfoDetails(request.user.id, request.projectId).then(([userResponse, rolesResponse]) => {
			if (cancelled) return;
			setUserDetails(userResponse);
			setDetails(rolesResponse);
			setBusy(false);
		}).catch(() => { if (!cancelled) { setUserDetails({ error: { message: msg("couldNotLoadUserDetails"), code: 0 } }); setBusy(false); } });

		return () => {
			cancelled = true;
		};
	}, [request, project?.id]);

	if (!request || project?.id !== request.projectId) return null;

	const closePopup = () => S_USER_INFO_POPUP.invoke(null);
	const detailsData = details?.data as Record<string, unknown> | undefined;
	const userDetailsData = userDetails?.data as Record<string, unknown> | undefined;
	const detailedUser = getDetailedUserPayload(detailsData);
	const detailedUserFromGet = getDetailedUserPayload(userDetailsData);
	const user = { ...request.user, ...detailedUser, ...detailedUserFromGet } as TAllUser;
	const clients = clientOverride ?? getUserClients(user);
	const rolesFromRolesGet = getDetailedRoles(detailsData);
	const roles = rolesFromRolesGet.length > 0 ? rolesFromRolesGet : getDetailedRoles(userDetailsData, request.user.roles);
	const accessStatus = membershipStatus(user.project_status);
	const canManageAccess = hasAnyRole(project.roles, ["admin"]);
	const refreshMembership = async () => {
		await request.onRolesUpdated();
		const [userResponse, rolesResponse] = await loadUserInfoDetails(request.user.id, request.projectId);
		if (requestRef.current !== request) return;
		setUserDetails(userResponse); setDetails(rolesResponse);
	};
	const extraFields = Object.entries(user).filter(([key, value]) => {
		return (
			!key.startsWith("$__") &&
			![
				"id",
				"name",
				"email",
				"roles",
				"client",
				"clients",
				"projects",
				"status",
				"status_email",
				"status_2fa",
				"type",
				"created_at",
				"updated_at",
				"deleted_at",
				"phone",
				"signin_attempts",
				"project_status",
				"project_id",
			].includes(key) &&
			isRenderableInfoValue(value)
		);
	});

	const editRoles = () => {
		S_USER_EDITOR_POPUP.invoke({
			userId: request.user.id,
			onSuccess: async () => {
				await request.onRolesUpdated();
				const [userResponse, rolesResponse] = await loadUserInfoDetails(request.user.id, request.projectId);
				if (requestRef.current !== request) return;
				setUserDetails(userResponse);
				setDetails(rolesResponse);
			},
		});
	};

	const refreshUserInfo = async (linkedClients: TClientFilterOption[]) => {
		setClientOverride(linkedClients);
		await request.onRolesUpdated();
	};

	const openLinkClientPopup = () => {
		S_USER_CLIENT_LINK_POPUP.invoke({
			user,
			linkedClients: clients,
			onChanged: refreshUserInfo,
		});
	};

	return (
		<Panel
			busy={busy}
			decorator="absolute"
			label={msg("userProfile")}
			onClose={closePopup}
			contentPadding="0px 20px 20px 20px"
			sx={{ width: "90vw", maxWidth: "700px", padding: "0", height: "80vh", maxHeight: "90vh", backgroundColor: "#f3f4f6" }}>
			<Spacer size="16px" />
			{(userDetails?.error || details?.error) && (
				<>
					<Error error={userDetails?.error ?? details?.error} />
					<Spacer size="12px" />
				</>
			)}
			<VBox gap="14px">
				<HBox
					gap="16px"
					sx={{ backgroundColor: "#fff", borderRadius: "10px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", alignItems: "center" }}>
					<Box
						sx={{
							width: "56px",
							height: "56px",
							borderRadius: "16px",
							backgroundColor: "#f8f0ed",
							border: "1px solid #eadcd5",
							color: "#a8441f",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "15px",
							fontWeight: 800,
							letterSpacing: "0.08em",
							flexShrink: 0,
						}}>
						{getUserInitials(user)}
					</Box>
					<VBox gap="5px" sx={{ flex: 1, minWidth: 0 }}>
						<Typo variant="head">{getUserDisplayName(user)}</Typo>
						<Typo variant="small" sx={{ color: "#6b7280" }}>
							{user.email}
						</Typo>
						<HBox gap="6px" sx={{ flexWrap: "wrap" }}>
							{roles.map(role => (
								<UserInfoBadge key={`${role.id}-${getRoleLabel(role)}`} label={getRoleLabel(role)} />
							))}
							{user.status && <UserInfoBadge label={String(user.status)} />}
						</HBox>
					</VBox>
					{accessStatus === "active" && canManageAccess && <Button variant="primarySmall" title={msg("editRoles")} onClick={editRoles} />}
				</HBox>

				<UserInfoSection title={msg("projectAccess")}>
					<UserInfoRow label={msg("project")} value={`${project.name} (ID ${project.id})`} />
					<UserInfoRow label={msg("access")} value={membershipLabel(user.project_status)} />
					{accessStatus === "deleted" && (
						<HBox gap="16px" sx={{ paddingTop: "14px", alignItems: "center", flexWrap: "wrap" }}>
							<Typo sx={{ flex: "1 1 240px", minWidth: 0, fontSize: "12px", color: "#7b746e", lineHeight: "18px" }}>{msg("restoringAccessWillReactivateTheSavedProjectRoles")}</Typo>
							{canManageAccess && (
								<Button
									variant="primarySmall"
									title={membershipBusy ? msg("restoring") : msg("restoreToProject")}
									disabled={busy || membershipBusy}
									sx={{ flexShrink: 0, marginLeft: "auto" }}
									onClick={() => void changeUserProjectAccess(user, refreshMembership, request.projectId, setMembershipBusy)}
								/>
							)}
						</HBox>
					)}
					{accessStatus === "blocked" && (
						<Typo sx={{ paddingTop: "14px", fontSize: "12px", color: "#7b746e", lineHeight: "18px" }}>{msg("projectAccessIsBlockedTheSavedRolesAreNotActive")}</Typo>
					)}
				</UserInfoSection>

				<UserInfoSection title={msg("account")}>
					<UserInfoRow label={msg("userId")} value={String(user.id)} mono />
					<UserInfoRow label={msg("email_969ccbd3")} value={user.email} />
					<UserInfoRow label={msg("personName")} value={getUserDisplayName(user)} />
					<UserInfoRow label={msg("type")} value={formatInfoValue(user.type)} />
					<UserInfoRow label={msg("phone")} value={formatInfoValue(user.phone)} />
					<UserInfoRow label={msg("signInAttempts")} value={formatInfoValue(user.signin_attempts)} />
				</UserInfoSection>

				<UserInfoSection title={msg("status")}>
					<UserInfoRow label={msg("userStatus")} value={formatInfoValue(user.status)} />
					<UserInfoRow label={msg("emailStatus")} value={formatInfoValue(user.status_email)} />
					<UserInfoRow label={msg("text2faStatus")} value={formatInfoValue(user.status_2fa)} />
					<UserInfoRow label={msg("created")} value={<DateTimeTooltip value={user.created_at as string}>{formatUserDate(user.created_at as string)}</DateTimeTooltip>} />
					<UserInfoRow label={msg("updated")} value={<DateTimeTooltip value={user.updated_at as string}>{formatInfoValue(user.updated_at ? formatUserDate(user.updated_at as string) : undefined)}</DateTimeTooltip>} />
				</UserInfoSection>

				<UserInfoSection title={msg("roles")}>
					{roles.length > 0 ? (
						<VBox gap="8px">
							{roles.map(role => (
								<HBox
									key={`${role.id}-${getRoleLabel(role)}`}
									sx={{
										padding: "9px 0",
										borderBottom: "1px solid #f0ece8",
										alignItems: "center",
										gap: "10px",
										"&:last-child": { borderBottom: "none" },
									}}>
									<UserInfoBadge label={getRoleLabel(role)} />
									<Typo sx={{ color: "#7b746e", fontSize: "12px" }}>{role.description ?? `ID ${role.id}`}</Typo>
								</HBox>
							))}
						</VBox>
					) : (
						<Typo sx={{ color: "#7b746e", fontSize: "13px" }}>{msg("noRolesReturned")}</Typo>
					)}
				</UserInfoSection>

				<UserInfoSection title={msg("clients")}>
					<HBox gap="10px" sx={{ alignItems: "center", paddingBottom: "12px", borderBottom: "1px solid #f0ece8", marginBottom: "4px" }}>
						<Typo sx={{ color: "#7b746e", fontSize: "12px", flex: 1 }}>
							{msg("linkedClientsCount", { count: clients.length })}
						</Typo>
						{accessStatus === "active" && canManageAccess && <Button variant="primarySmall" title={msg("manageClients")} onClick={openLinkClientPopup} />}
					</HBox>
					{clients.length > 0 ? (
						<VBox gap="8px">
							{clients.map(client => (
								<HBox
									key={client.id}
									sx={{
										padding: "9px 0",
										borderBottom: "1px solid #f0ece8",
										alignItems: "center",
										gap: "10px",
										"&:last-child": { borderBottom: "none" },
									}}>
									<ClientCountryFlag client={client} />
									<Typo sx={{ flex: 1, color: "#1f1d1b", fontSize: "13px", fontWeight: 600 }}>{getClientLabel(client)}</Typo>
									<Typo sx={{ color: "#7b746e", fontSize: "12px" }}>ID {client.id}</Typo>
								</HBox>
							))}
						</VBox>
					) : (
						<Typo sx={{ color: "#7b746e", fontSize: "13px" }}>{msg("noClientReturned")}</Typo>
					)}
				</UserInfoSection>

				{extraFields.length > 0 && (
					<UserInfoSection title={msg("additionalServerFields")}>
						{extraFields.map(([key, value]) => (
							<UserInfoRow key={key} label={getFieldLabel(key)} value={formatInfoValue(value)} />
						))}
					</UserInfoSection>
				)}
			</VBox>

			<Spacer size="20px" />
			<HBox gap="12px" sx={{ width: "100%" }}>
				<Spacer grow />
				<Button variant="secondary" title={msg("close")} onClick={closePopup} />
				{accessStatus === "active" && canManageAccess && <Button variant="primary" title={msg("editRoles")} onClick={editRoles} />}
			</HBox>
		</Panel>
	);
}

function UserClientLinkPopup() {
	useLocale();
	const request = S_USER_CLIENT_LINK_POPUP.use();
	const [selectedClient, setSelectedClient] = useState<TClientFilterOption | null>(null);
	const [linkedClients, setLinkedClients] = useState<TClientFilterOption[]>([]);
	const [busy, setBusy] = useState(false);
	const [allClients] = GD.REQ_CLIENTS.useRequest(undefined, {});

	useEffect(() => {
		setSelectedClient(null);
		setLinkedClients(request?.linkedClients ?? []);
	}, [request?.user.id, request?.linkedClients]);

	if (!request) return null;

	const closePopup = () => S_USER_CLIENT_LINK_POPUP.invoke(null);
	const linkedClientIds = new Set(linkedClients.map(client => client.id));
	const clientOptions = normalizeClientFilterOptions(allClients?.data).filter(client => !linkedClientIds.has(client.id));
	const userName = getUserDisplayName(request.user);

	const refreshLinkedClients = async () => {
		setSelectedClient(null);
	};

	const linkClient = async () => {
		if (!selectedClient) return;

		setBusy(true);
		const response = await GD.REQ_API.request({
			method: "clients/user/link",
			data: {
				client_id: selectedClient.id,
				user_id: request.user.id,
			},
		});
		setBusy(false);

		if (response.error) {
			notify.error(response.error.message);
			return;
		}

		notify.info(msg("clientLinked"));
		const nextClients = [...linkedClients.filter(client => client.id !== selectedClient.id), selectedClient];
		setLinkedClients(nextClients);
		await request.onChanged(nextClients);
		await refreshLinkedClients();
	};

	const unlinkClient = async (client: TClientFilterOption) => {
		setBusy(true);
		const response = await GD.REQ_API.request({
			method: "clients/user/unlink",
			data: {
				client_id: client.id,
				user_id: request.user.id,
			},
		});
		setBusy(false);

		if (response.error) {
			notify.error(response.error.message);
			return;
		}

		notify.info(msg("clientUnlinked"));
		const nextClients = linkedClients.filter(linkedClient => linkedClient.id !== client.id);
		setLinkedClients(nextClients);
		await request.onChanged(nextClients);
		await refreshLinkedClients();
	};

	return (
		<Panel
			busy={busy}
			decorator="absolute"
			growContent
			sx={{ width: "90vw", maxWidth: "620px", maxHeight: "90vh", padding: "0", backgroundColor: "#f3f4f6" }}
			contentPadding="0"
			onClose={closePopup}
			label={msg("userClients")}>
			<VBox
				gap="14px"
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					padding: "20px",
					paddingRight: "22px",
				}}>
				<Box
					sx={{
						backgroundColor: "#fff",
						border: "1px solid #e6ddd8",
						borderRadius: "12px",
						padding: "16px",
						boxShadow: "0 6px 18px rgba(59, 43, 32, 0.05)",
					}}>
					<HBox gap="14px" sx={{ alignItems: "center" }}>
						<Box
							sx={{
								width: "42px",
								height: "42px",
								borderRadius: "12px",
								backgroundColor: "#f8f0ed",
								border: "1px solid #eadcd5",
								color: "#a8441f",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: "12px",
								fontWeight: 800,
								letterSpacing: "0.08em",
								flexShrink: 0,
							}}>
							{getUserInitials(request.user)}
						</Box>
						<VBox gap="3px" sx={{ minWidth: 0, flex: 1 }}>
							<Typo
								sx={{
									color: "#111827",
									fontSize: "14px",
									fontWeight: 700,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{userName}
							</Typo>
							<Typo sx={{ color: "#6b7280", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
								{request.user.email}
							</Typo>
						</VBox>
						<Typo sx={{ color: "#7b746e", fontSize: "12px", whiteSpace: "nowrap" }}>{linkedClients.length}{msg("linked_0b29126c")}</Typo>
					</HBox>
				</Box>

				<Box
					sx={{
						backgroundColor: "#fff",
						border: "1px solid #e6ddd8",
						borderRadius: "12px",
						padding: "16px",
						boxShadow: "0 6px 18px rgba(59, 43, 32, 0.05)",
					}}>
					<VBox gap="10px">
						<Typo sx={{ color: "#4a4a4a", fontSize: "11px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>{msg("linkClient")}</Typo>
						<HBox gap="10px" sx={{ alignItems: "end", flexWrap: "wrap" }}>
							<Box sx={{ flex: 1, minWidth: "240px" }}>
								<Select<TClientFilterOption>
									sx={{ width: "100%" }}
									size="small"
									label={msg("client")}
									value={selectedClient ?? undefined}
									values={clientOptions}
									onChange={value => setSelectedClient(value ?? null)}
									disabled={busy || clientOptions.length === 0}
								/>
							</Box>
							<Button variant="primarySmall" title={busy ? msg("linking") : msg("link")} disabled={busy || !selectedClient} onClick={linkClient} />
						</HBox>
						{clientOptions.length === 0 && <Typo sx={{ color: "#7b746e", fontSize: "12px" }}>{msg("allClientsAreAlreadyLinked")}</Typo>}
					</VBox>
				</Box>

				<Box
					sx={{
						backgroundColor: "#fff",
						border: "1px solid #e6ddd8",
						borderRadius: "12px",
						padding: "16px",
						boxShadow: "0 6px 18px rgba(59, 43, 32, 0.05)",
						display: "flex",
						flexDirection: "column",
					}}>
					<VBox gap="10px">
						<Typo sx={{ color: "#4a4a4a", fontSize: "11px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>{msg("linkedClients")}</Typo>
						<VBox>
							{linkedClients.length > 0 ? (
								linkedClients.map(client => (
									<HBox
										key={client.id}
										sx={{
											padding: "9px 0",
											borderBottom: "1px solid #f0ece8",
											alignItems: "center",
											gap: "10px",
											"&:last-child": { borderBottom: "none" },
										}}>
										<ClientCountryFlag client={client} />
										<VBox gap="2px" sx={{ flex: 1, minWidth: 0 }}>
											<Typo
												sx={{
													color: "#1f1d1b",
													fontSize: "13px",
													fontWeight: 600,
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}>
												{getClientLabel(client)}
											</Typo>
										</VBox>
										<Button variant="secondarySmall" title={msg("unlink")} disabled={busy} onClick={() => unlinkClient(client)} />
									</HBox>
								))
							) : (
								<Typo sx={{ color: "#7b746e", fontSize: "13px" }}>{msg("noLinkedClients")}</Typo>
							)}
						</VBox>
					</VBox>
				</Box>

				<HBox gap="10px" sx={{ justifyContent: "flex-end" }}>
					<Button variant="secondary" title={msg("close")} onClick={closePopup} />
				</HBox>
			</VBox>
		</Panel>
	);
}

const UserDirectoryTab = ({
	baseRequest = {},
	onRemove,
}: {
	baseRequest?: Record<string, unknown>;
	onRemove?: (user: TAllUser, reload: () => void, projectId: number) => void | Promise<void>;
}) => {
	useLocale();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [searchObject, setSearchObject] = useState<{ search: string }>({ search: "" });
	const [clientFilter, setClientFilter] = useState<TClientFilterOption>(allClientOption);
	const [roleFilter, setRoleFilter] = useState<TUserFilterOption>(allRoleOption);
	const [roleOptions, setRoleOptions] = useState<TUserFilterOption[]>([allRoleOption]);
	const [request, setRequest] = useState(getUserListRequest("", allClientOption, allRoleOption, baseRequest));
	const [users, reloadUsers, _, busy] = GD.REQ_USERS.useRequest(undefined, request);
	const [clients] = GD.REQ_CLIENTS.useRequest(undefined, {});
	const [filterCollapsed, setFilterCollapsed] = useState(false);

	const clientOptions = [allClientOption, ...normalizeClientFilterOptions(clients?.data)];
	const usersList = (users?.data?.map(user => ({ ...user, roles: user.roles ?? [] })) ?? []) as TAllUser[];

	useEffect(() => {
		if (!users?.data) return;

		setRoleOptions(prev => {
			const optionsByValue = new Map(prev.map(option => [option.value, option]));

			users.data.forEach(user => {
				user.roles?.forEach(role => {
					optionsByValue.set(String(role.id), { name: role.role, value: String(role.id) });
				});
			});

			const next = [
				allRoleOption,
				...Array.from(optionsByValue.values())
					.filter(option => option.value)
					.sort((a, b) => a.name.localeCompare(b.name)),
			];

			const sameOptions =
				next.length === prev.length && next.every((option, index) => option.value === prev[index].value && option.name === prev[index].name);
			return sameOptions ? prev : next;
		});
	}, [users]);

	const handleSearch = () => {
		const nextRequest = getUserListRequest(searchObject.search, clientFilter, roleFilter, baseRequest);
		setRequest(nextRequest);
		reloadUsers(nextRequest);
		setSearchObject({ search: searchObject.search });
	};

	const resetSearch = () => {
		const nextRequest = getUserListRequest("", allClientOption, allRoleOption, baseRequest);
		setSearchObject({ search: "" });
		setClientFilter(allClientOption);
		setRoleFilter(allRoleOption);
		setRequest(nextRequest);
		reloadUsers(nextRequest);
	};

	return (
		<>
			<VBox sx={{ padding: "0", minHeight: "100%" }}>
				{filterCollapsed ? (
					<Box
						sx={{
							flexShrink: 0,
							background: "#fff",
							borderRadius: "14px",
							boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
							marginBottom: "15px",
						}}>
						<HBox gap="10px" sx={{ alignItems: "center", padding: "10px 14px", flexWrap: "wrap" }}>
							<Input
								sx={{ flex: "1", minWidth: "190px" }}
								size="small"
								type="search"
								name="search"
								placeholder={msg("searching")}
								data={searchObject}
								label={msg("search")}
							/>
							<HBox gap="8px" sx={{ alignItems: "center", alignSelf: "end", height: "36px" }}>
								<Button onClick={() => handleSearch()} title={msg("search")} variant="primarySmall" />
								<ExpandPill label={msg("filters")} icon={<FilterIcon size={14} />} onClick={() => setFilterCollapsed(false)} />
							</HBox>
						</HBox>
					</Box>
				) : (
					<VBox
						sx={{
							flexShrink: 0,
							background: "#fff",
							borderRadius: "14px",
							boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
							padding: "14px 20px",
							gap: "10px",
							marginBottom: "15px",
						}}>
						<HBox sx={{ justifyContent: "space-between", alignItems: "center", minHeight: "16px" }}>
							<Typo
								sx={{
									fontSize: "10px",
									color: "#94a3b8",
									textTransform: "uppercase",
									letterSpacing: "0.08em",
									fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
									fontWeight: 600,
									whiteSpace: "nowrap",
								}}>{msg("filter")}</Typo>
							<Box
								onClick={() => setFilterCollapsed(true)}
								title={msg("collapseFilter")}
								sx={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									width: "22px",
									height: "22px",
									borderRadius: "5px",
									color: "#94a3b8",
									cursor: "pointer",
									transition: "background-color 0.15s, color 0.15s",
									"&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
								}}>
								<ChevronUp size={14} />
							</Box>
						</HBox>
						<HBox gap="8px" sx={{ flexWrap: "wrap", alignItems: "start", flex: "1 1 auto" }}>
							<Input
								sx={{ flex: "1", minWidth: "190px" }}
								size="small"
								type="search"
								name="search"
								placeholder={msg("searching")}
								data={searchObject}
								label={msg("search")}
							/>
							<Select<TClientFilterOption>
								sx={{ minWidth: "190px", maxWidth: "260px" }}
								size="small"
								label={msg("client")}
								value={clientFilter}
								values={clientOptions}
								minSheetWidth="360px"
								onChange={value => setClientFilter(value ?? allClientOption)}
							/>
							<Select<TUserFilterOption>
								sx={{ minWidth: "160px", maxWidth: "220px" }}
								size="small"
								label={msg("role")}
								value={roleFilter}
								values={roleOptions}
								onChange={value => setRoleFilter(getFilterOptionByValue(roleOptions, value as unknown as string) ?? allRoleOption)}
							/>
							<HBox gap="10px" sx={{ alignSelf: "end", height: "36px", marginLeft: "auto" }}>
								<Spacer grow />
								<Button onClick={() => resetSearch()} variant="secondarySmall" title={msg("reset")} />
								<Button onClick={() => handleSearch()} title={msg("search")} variant="primarySmall" />
							</HBox>
						</HBox>
					</VBox>
				)}
				<AllUsersTable
					error={users?.error}
					busy={busy}
					users={usersList}
					onEdit={user => {
						if (project) S_USER_INFO_POPUP.invoke({ user, projectId: project.id, onRolesUpdated: () => reloadUsers({ ...request }) });
					}}
					onRemove={onRemove && project && hasAnyRole(project.roles, ["admin"]) ? user => onRemove(user, () => reloadUsers({ ...request }), project.id) : undefined}
				/>
			</VBox>
		</>
	);
};

const getMembershipContext = async () => {
	const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
	return { userId: profile?.id ?? 0, projectId: project?.id ?? 0, projectName: project?.name ?? "", canManage: !!project && hasAnyRole(project.roles, ["admin"]) };
};

const updateMembership = createMembershipUpdater({ context: getMembershipContext, request: req => GD.REQ_API.request(req) });

const changeUserProjectAccess = async (user: TAllUser, reloadUsers: () => unknown, projectId: number, setBusy?: (busy: boolean) => void) => {
	try {
		const expected = await getMembershipContext();
		if (!expected.canManage || !expected.userId || expected.projectId !== projectId) throw new globalThis.Error(msg("projectOrSessionChangedReopenTheUserInTheSelectedProject"));
		const status = membershipStatus(user.project_status);
		if (status === "unknown") throw new globalThis.Error(msg("projectAccessIsUnknownRefreshTheUserBeforeContinuing"));
		const restore = status === "deleted";
		const projectLabel = `${expected.projectName} (ID ${projectId})`;
		S_CONFIRM.invoke({
			title: restore ? msg("restoreToProject") : msg("removeFromProject"),
			message: restore
				? msg("templateRestoreToAllSavedProjectRolesIncludingAdministratorAcces_ad665068", { v0: getUserDisplayName(user), v1: user.email, v2: projectLabel })
				: msg("templateRemoveFromTheyWillLoseAccessToThisProjectTheirAccountAnd_ce215e16", { v0: getUserDisplayName(user), v1: user.email, v2: projectLabel }),
			confirm: restore ? msg("restore") : msg("remove"),
			reject: "Cancel",
			onResult: async val => {
				if (val !== "confirm") return;
				setBusy?.(true);
				try {
					const result = await updateMembership(user.id, restore ? "restore" : "remove", expected);
					if (result === "pending") return;
					notify.info(restore ? msg("userIsActiveInThisProject") : msg("userIsRemovedFromThisProject"));
					try { await reloadUsers(); }
					catch { notify.error(msg("projectAccessUpdatedButTheViewCouldNotRefreshReloadThePage")); }
				} catch (error) {
					notify.error(error instanceof globalThis.Error ? error.message : msg("couldNotUpdateProjectAccessRefreshTheUserAndTryAgain"));
				} finally { setBusy?.(false); }
			},
		});
	} catch (error) {
		notify.error(error instanceof globalThis.Error ? error.message : msg("couldNotCheckProjectAccess"));
	}
};

const AllUsersTab = () => {
	useLocale();
	return <UserDirectoryTab baseRequest={{ include_deleted: true }} onRemove={changeUserProjectAccess} />;
};

const ManagementTab = () => {
	useLocale();
	const request = { excludedRoles: [2] };

	return <UserDirectoryTab baseRequest={request} onRemove={changeUserProjectAccess} />;
};

type TRolesProps = { roles?: IUserRole[] };

const Roles = ({ roles }: TRolesProps) => {
	useLocale();
	// const [roles, setRoles] = useState<IUserRole[]>();

	// useEffect(() => {
	// 	const loadRoles = async () => {
	// 		const roles = await GD.REQ_API.request({
	// 			method: "users/roles/get",

	// 			data: {
	// 				user_id: id,
	// 			},
	// 		});
	// 		if (roles.data) {
	// 			setRoles(roles.data.roles);
	// 		}
	// 	};

	// 	loadRoles();
	// }, [id]);

	return (
		<HBox gap="4px" sx={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
			{roles &&
				roles.map(r => (
					<Typo
						key={r.id}
						sx={{
							backgroundColor: "#f8f0ed",
							border: "1px solid #eadcd5",
							borderRadius: "999px",
							padding: "3px 8px",
							color: "#2c2926",
							fontSize: "11px",
							fontWeight: 600,
						}}>
						{r.role}
					</Typo>
				))}
		</HBox>
	);
};

const InvitesTab: TFeaturedComponent<{ invitesRef: MutableRefObject<TInvitesHandle | null> }> = ({ invitesRef }) => {
	useLocale();
	return <Invites ref={invitesRef} />;
};
InvitesTab.feature = "invites";

type TInvitesProps = {};

const inviteStatusOptions: { name: string; status: IInvite["status"] | "" }[] = [
	{ get name() { return msg("allStatuses"); }, status: "" },
	{ get name() { return msg("created"); }, status: "created" },
	{ get name() { return msg("visited"); }, status: "visited" },
	{ get name() { return msg("completed"); }, status: "completed" },
];
const inviteTypeOptions: { name: string; userType: IInvite["user_type"] | "" }[] = [
	{ get name() { return msg("allTypes"); }, userType: "" },
	{ get name() { return msg("external"); }, userType: "external" },
	{ get name() { return msg("internal"); }, userType: "internal" },
];

const Invites = forwardRef<TInvitesHandle, TInvitesProps>(({}: TInvitesProps, ref) => {
	const [invites, updateInvites, __, busy] = GD.REQ_INVITES.useRequest(undefined, {});
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState(inviteStatusOptions[0]);
	const [typeFilter, setTypeFilter] = useState(inviteTypeOptions[0]);
	//!TODO: _busyRemove;
	const [_busyRemove, setBusyRemove] = useState<boolean>(false);

	useImperativeHandle(ref, () => ({
		update() {
			updateInvites({});
		},
	}));

	const onRemove = async (invite: IInvite) => {
		setBusyRemove(true);

		const req = await GD.REQ_API.request({
			method: "users/invite/cancel",
			data: {
				token: invite.token,
			},
		});

		if (req.error) {
			notify.error(() => {
				return (
					<VBox gap="8px">
						<Typo variant="text">{req.error.message}</Typo>
						{req.error.details && <Typo variant="medium">{req.error.details}</Typo>}
					</VBox>
				);
			});
		} else {
			notify.info(msg("inviteCancelled"));
			updateInvites({});
		}

		setBusyRemove(false);
	};

	type TInviteTableRow = IInvite & { __actions: string };
	const allInvites = invites?.data ?? [];
	const searchTerm = search.trim().toLowerCase();
	const hasFilters = Boolean(searchTerm || statusFilter.status || typeFilter.userType);
	const inviteRows: TInviteTableRow[] = allInvites
		.filter(invite =>
			invite.email.toLowerCase().includes(searchTerm) &&
			(!statusFilter.status || invite.status === statusFilter.status) &&
			(!typeFilter.userType || invite.user_type === typeFilter.userType),
		)
		.map(invite => ({ ...invite, __actions: "" }));
	const resetFilters = () => {
		setSearch("");
		setStatusFilter(inviteStatusOptions[0]);
		setTypeFilter(inviteTypeOptions[0]);
	};

	return (
		<>
			<HBox
				gap="10px"
				sx={{
					flexShrink: 0,
					flexWrap: "wrap",
					alignItems: "end",
					background: "#fff",
					borderRadius: "14px",
					boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
					padding: "14px 20px",
					marginBottom: "15px",
				}}>
				<Input
					sx={{ flex: "1", minWidth: "190px" }}
					size="small"
					type="search"
					name="search"
					label={msg("email_969ccbd3")}
					placeholder={msg("searchByEmail")}
					data={{ search }}
					onChange={setSearch}
				/>
				<Select
					sx={{ minWidth: "160px" }}
					size="small"
					label={msg("status")}
					value={statusFilter}
					values={inviteStatusOptions}
					onChange={value => setStatusFilter(value ?? inviteStatusOptions[0])}
				/>
				<Select
					sx={{ minWidth: "160px" }}
					size="small"
					label={msg("type")}
					value={typeFilter}
					values={inviteTypeOptions}
					onChange={value => setTypeFilter(value ?? inviteTypeOptions[0])}
				/>
				<Button onClick={resetFilters} variant="secondarySmall" title={msg("reset")} />
			</HBox>
			<Error error={invites?.error} />
			{invites?.data && !invites.error && (
				<Typo sx={{ color: "#6b7280", fontSize: "12px", marginBottom: "10px" }}>
					{hasFilters ? msg("templateOfInvites_c1c498d6", { v0: inviteRows.length, v1: allInvites.length }) : msg("templateInvites_f1cc4f13", { v0: allInvites.length })}
				</Typo>
			)}
			{(busy || _busyRemove) && inviteRows.length === 0 ? (
				<Typo sx={{ color: "#6b6762", fontSize: "13px", padding: "30px" }}>{_busyRemove ? msg("updatingInvites") : msg("loadingInvites")}</Typo>
			) : (
				<Table<TInviteTableRow>
					headPadding="14px 12px"
					cellPadding="8px 12px"
					headSx={usersTableHeadSx}
					sx={usersTableSx}
					scrollable={false}
					order={["email", "user_type", "status", "created_at", "updated_at", "__actions"]}
					headers={[
						{ value: "email", gridColumn: "minmax(220px, 1fr)" },
						{ value: "user_type", gridColumn: "max-content" },
						{ value: "status", gridColumn: "max-content" },
						{ value: "created_at", gridColumn: "max-content" },
						{ value: "updated_at", gridColumn: "max-content" },
						{ value: "__actions", gridColumn: "max-content" },
					]}
					gridTemplateColumns="minmax(220px, 1fr) max-content max-content max-content max-content max-content"
					headerFactory={usersTableHeaderFactory}
					cellFactory={(invite, cell, _idx, _key, name) => {
						if (name === "email") return { content: <Typo sx={{ color: "#111827", fontSize: "13px", fontWeight: 500 }}>{invite.email}</Typo> };
						if (name === "user_type")
							return { content: <Typo sx={{ color: "#6b7280", fontSize: "12px", textTransform: "capitalize" }}>{invite.user_type}</Typo> };
						if (name === "status") return { content: <AllUsersStatusBadge status={invite.status} /> };
						if (name === "created_at" || name === "updated_at")
							return { content: <Typo sx={{ color: "#6b7280", fontSize: "12px" }}><DateTimeTooltip value={cell}>{formatUserDate(cell)}</DateTimeTooltip></Typo> };
						if (name === "__actions")
							return {
								content: (
									<TableActionButton
										title={invite.status !== "completed" ? msg("cancel") : msg("delete")}
										variant="danger"
										onClick={() => onRemove(invite)}
									/>
								),
							};
						return { content: cell };
					}}
					data={inviteRows}
				/>
			)}
			{!busy && !_busyRemove && !invites?.error && invites?.data && inviteRows.length === 0 && (
				<Typo sx={{ color: "#6b6762", fontSize: "13px", padding: "30px", textAlign: "center" }}>
					{hasFilters ? msg("noInvitesMatchYourFiltersTryChangingOrResettingThem") : msg("noInvitesYet")}
				</Typo>
			)}
		</>
	);
});

const RightSectionUsers = ({}) => {
	return null;
	// return (
	// 	<Box
	// 		sx={{
	// 			display: "flex",
	// 			flex: 1,
	// 			minHeight: "0px",
	// 			flexDirection: "column",

	// 			gap: "10px",
	// 		}}>
	// 		{/* <Comments sectionType={0} targetId={1} targetType="client" />
	// 		<EventsClients target_id={1} target_type="client" /> */}
	// 	</Box>
	// );
};

const ApiUsersTab: TFeaturedComponent<{ reloadRef: MutableRefObject<() => void> }> = ({ reloadRef }) => {
	useLocale();
	return <ApiUsers reloadRef={reloadRef} />;
};
ApiUsersTab.feature = "api_users";

type IApiKey = {
	kid: string;
	name: string;
	status: "active" | "disabled";
	user_id: number;
	project_id: number;
	endpoints: string[] | null;
	created_at: string;
	expires_at: string;
};

const ApiUsers = ({ reloadRef }: { reloadRef: MutableRefObject<() => void> }) => {
	useLocale();
	const [keys, setKeys] = useState<IAPIResponse<IApiKey[]>>();
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		reload();
	}, []);

	const reload = async () => {
		setBusy(true);
		const req = await GD.REQ_API.request({ method: "api-keys/list", data: {} });
		setKeys(req as IAPIResponse<IApiKey[]>);
		setBusy(false);
	};

	useEffect(() => {
		reloadRef.current = reload;
	});

	const onEditApiUser = (row: IApiKey) => {
		S_API_USER_POPUP.invoke({ kid: row.kid, userId: row.user_id, onSuccess: reload });
	};

	const onSetStatus = async (kid: string, status: "active" | "disabled") => {
		await GD.REQ_API.request({
			method: "api-keys/set-status",
			data: { kid, status },
		});
		reload();
	};

	type TApiKeyTableRow = IApiKey & { key_id: string; project: string; __actions: string };
	const apiKeyRows: TApiKeyTableRow[] = (keys?.data ?? []).map(row => ({ ...row, key_id: "", project: "", __actions: "" }));

	return (
		<>
			<Spacer size="10px" />
			<Error error={keys?.error} />
			{busy && apiKeyRows.length === 0 ? (
				<Typo sx={{ color: "#6b6762", fontSize: "13px", padding: "30px" }}>{msg("loadingApiUsers")}</Typo>
			) : (
				<Table<TApiKeyTableRow>
					headPadding="14px 12px"
					cellPadding="8px 12px"
					headSx={usersTableHeadSx}
					sx={usersTableSx}
					scrollable={false}
					order={["name", "key_id", "user_id", "project", "endpoints", "status", "expires_at", "created_at", "__actions"]}
					headers={[
						{ value: "name", gridColumn: "minmax(170px, 1fr)" },
						{ value: "key_id", gridColumn: "minmax(180px, 1fr)" },
						{ value: "user_id", gridColumn: "max-content" },
						{ value: "project", gridColumn: "max-content" },
						{ value: "endpoints", gridColumn: "max-content" },
						{ value: "status", gridColumn: "max-content" },
						{ value: "expires_at", gridColumn: "max-content" },
						{ value: "created_at", gridColumn: "max-content" },
						{ value: "__actions", gridColumn: "max-content" },
					]}
					gridTemplateColumns="minmax(170px, 1fr) minmax(180px, 1fr) max-content max-content max-content max-content max-content max-content max-content"
					headerFactory={usersTableHeaderFactory}
					onRowClick={row => onEditApiUser(row)}
					cellFactory={(row, cell, _idx, _key, name) => {
						if (name === "name") {
							return {
								content: (
									<VBox gap="2px">
										<Typo sx={{ color: "#111827", fontSize: "13px", fontWeight: 500 }}>{row.name || msg("apiUser")}</Typo>
										<Typo sx={{ color: "#6b7280", fontSize: "11px", lineHeight: "15px" }}>{msg("user")}{row.user_id}</Typo>
									</VBox>
								),
							};
						}
						if (name === "key_id")
							return {
								content: <Typo sx={{ color: "#4b5563", fontSize: "12px", fontFamily: "monospace", wordBreak: "break-all" }}>{row.kid}</Typo>,
							};
						if (name === "user_id") return { content: <Typo sx={{ color: "#6b7280", fontSize: "12px" }}>{row.user_id}</Typo> };
						if (name === "project") return { content: <Typo sx={{ color: "#6b7280", fontSize: "12px" }}>{row.project_id || "-"}</Typo> };
						if (name === "endpoints")
							return {
								content: (
									<Typo sx={{ color: "#6b7280", fontSize: "12px" }}>
										{row.endpoints?.length ? msg("templateEndpoints_917c1e77", { v0: row.endpoints.length }) : msg("allEndpoints")}
									</Typo>
								),
							};
						if (name === "status") return { content: <AllUsersStatusBadge status={row.status} /> };
						if (name === "expires_at" || name === "created_at")
							return { content: <Typo sx={{ color: "#6b7280", fontSize: "12px" }}><DateTimeTooltip value={cell}>{formatUserDate(cell)}</DateTimeTooltip></Typo> };
						if (name === "__actions") {
							return {
								content: (
									<HBox gap="8px" sx={{ justifyContent: "flex-end" }}>
										<TableActionButton title={msg("edit")} onClick={() => onEditApiUser(row)} />
										<TableActionButton
											title={row.status === "active" ? msg("disable") : msg("enable")}
											onClick={() => onSetStatus(row.kid, row.status === "active" ? "disabled" : "active")}
										/>
									</HBox>
								),
							};
						}
						return { content: cell };
					}}
					data={apiKeyRows}
				/>
			)}
		</>
	);
};
